import React, { Component, useState, useEffect, useRef, useImperativeHandle, useMemo, forwardRef } from "react";
import { cloneDeep } from "lodash";
import { Button, Dialog, Icon } from "@alifd/next";

import { JsonToDomService } from "./service";
import { t } from "../../locale/index";

import "./index.less";
import { VirtualArrayKey, VirtualDomItemType, VirtualDomNode } from "./type";
import JsonViewer from "./JsonViewer";

interface JsonModalProps {
  showType: "button" | 'icon'
  json: any;
  value: string;
  onChange: (value: any) => void;
}


const JsonModal = forwardRef((props: any, ref) => {
  const { json, value } = props;
  const jsonToDomService = new JsonToDomService();
  const [rootData, setRootData] = useState<VirtualDomItemType | null>(null);
  const [selectedUuid, setSelectedUuid] = useState<string>(""); //选中的叶子节点，单选
  const [selectedPath, setSelectedPath] = useState<VirtualDomItemType[]>([]);

  useEffect(() => {
    const data = jsonToDomService.getVirtualDomByJson(json);
    setRootData(data);
    //当value存在时，进行回显选中数据
    //规则： 按照名称递归查找数据  数组：当下标在当前数据不存在，则展示第一个数据，若存在，则对应勾选对应的下标
    if (value) {
      const path = findPathInTree(data, value);
      setSelectedUuid(path[path.length - 1].uuid);
      setSelectedPath(path);
    }
  }, []);

  //递归遍历节点并设置选中节点以及选中路径
  function findPathInTree(root: VirtualDomItemType, expression: string) {
    const parts = expression.split(/(\[\d+\]|\.)/);
    let currentNode = root;
    let path = [];

    for (let part of parts) {
      if (part.startsWith('[') && part.endsWith(']')) {
        // 处理数组索引
        const index = parseInt(part.slice(1, -1), 10);
        if (index >= 0 && index < currentNode.children.length) {    //下标范围内
          currentNode.arrayValue = index;
          path.push(currentNode);
          currentNode = currentNode.children[index];
        } else {    //下标范围外默认0
          currentNode.arrayValue = index;
          path.push(currentNode);
          currentNode = currentNode.children[0];
        }
      } else if (part.length > 0) {
        // 处理对象键
        const foundChild = currentNode.children?.find(child => child.key === part);
        if (foundChild) {
          path.push(currentNode);
          currentNode = foundChild;
        } else {
          console.log(`Node ${part} not found under ${currentNode.value}`)
        }
      }
    }

    path.push(currentNode);
    return path;
  }

  const expressionData = useMemo(() => {
    let str = "";
    for (let i = 1; i < selectedPath.length; i++) {
      if (selectedPath[i].type === VirtualDomNode.array) {
        // 数组
        str += `${i > 1 ? '.' : ''}${selectedPath[i].key}[${selectedPath[i].arrayValue || 0}]`; //数组下标默认0，下拉框选择了变量，则下标为变量(后期需要正常的下标，可以给node节点添加index展示index即可)
      } else if (selectedPath[i].key) {
        // 对象
        str += `${i > 1 ? '.' : ''}${selectedPath[i].key}`;
      }
    }
    return str;
  }, [selectedPath]);

  //递归便利节点，选中节点并给出路径
  const findPathDFS = (
    root: VirtualDomItemType | null,
    targetUuid: string,
    isChecked: boolean
  ) => {
    function dfs(
      node: VirtualDomItemType | null,
      path: VirtualDomItemType[]
    ): boolean {
      if (!node) return false;
      path.push(node);
      if (node.uuid === targetUuid) {
        // 更新选中数据
        setSelectedUuid(isChecked ? node.uuid : "");
        if (!isChecked) setSelectedPath([]);
        return true; //结束递归
      }
      if (node.children?.length > 0) {
        for (let i = 0; i < node.children.length; i++) {
          if (dfs(node.children[i], path)) {
            return true;
          }
        }
      }
      path.pop();
      return false;
    }

    const path: VirtualDomItemType[] = [];
    if (dfs(root, path)) {
      isChecked && setSelectedPath(path);
      !isChecked && setSelectedPath([]);
    }
  };

  //递归遍历树，更新数据的arrayKey

  const handleUpdateTree = (
    root: VirtualDomItemType | null,
    targetUuid: string,
    value: VirtualArrayKey
  ) => {
    const handleChangeArrkey = (
      root: VirtualDomItemType | null,
      targetUuid: string,
      value: VirtualArrayKey
    ) => {
      if (!root) return false;

      if (root.uuid === targetUuid) {
        //更新数据源
        root.arrayValue = value;
        //更新选中数据路径
        for (let i = 0; i < selectedPath.length; i++) {
          if (selectedPath[i].uuid === targetUuid) {
            selectedPath[i].arrayValue = value;
            setSelectedPath(cloneDeep(selectedPath))
            break;
          }
        }
        return root;
      }

      if (root.children) {
        for (let child of root.children) {
          if (handleChangeArrkey(child, targetUuid, value)) {
            return root;
          }
        }
      }

      return null;
    };
    const updatedTree = handleChangeArrkey(root, targetUuid, value);
    if (updatedTree) {
      setRootData(cloneDeep(updatedTree));
    }
  };

  useImperativeHandle(
    ref,
    () => {
      return {
        expressionData: expressionData,
      };
    },
    [expressionData]
  );


  return (
    <JsonViewer
      rootNode={rootData}
      selectedUuid={selectedUuid}
      onSelect={findPathDFS.bind(null, rootData)}
      expression={expressionData}
      onChangeArrKey={handleUpdateTree.bind(null, rootData)}
    ></JsonViewer>
  );
})


const JsonToDomSetter = (props: JsonModalProps) => {
  const { json, value, onChange } = props;

  const [showType] = useState(props.showType || 'button')
  const modalRef = useRef<any>();
  const [visible, setVisible] = useState(false);
  const showDialog = () => {
    setVisible(true);
  }

  const closeDialog = () => {
    setVisible(false);
  }



  return (
    <div>
      {showType === 'button' && <Button onClick={showDialog}>{t("jsonToDomSetter.title")}</Button>}
      {showType === 'icon' && <Icon onClick={showDialog} className="json-dialog-icon" type="edit" />}
      <Dialog
        title={t("jsonToDomSetter.title")}
        footerAlign="right"
        visible={visible}
        className="json-dialog-box"
        overflowScroll={false}
        footer={
          [
            <Button onClick={() => {
              onChange(modalRef.current?.expressionData)
              closeDialog();
            }} type="primary">{t("jsonToDomSetter.confirm")}</Button>,
            <Button style={{ marginLeft: '10px' }} onClick={() => { setVisible(false) }}>{t("jsonToDomSetter.cancel")}</Button>
          ]
        }
        onClose={closeDialog}
      >
        <JsonModal json={json} value={value} onChange={onChange} ref={modalRef} />
      </Dialog>
    </div>

  )
}


export default class extends Component<JsonModalProps> {
  render(): React.ReactNode {
    return <JsonToDomSetter {...this.props} />;
  }
}
